import React, { lazy, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { Analytics } from "@vercel/analytics/react";
import { Helmet } from "react-helmet";
import { FeatureContextTracker } from "./hooks/useFeatureContext";
import ScrollToTop from "./components/ScrollToTop";
// Captures UTM/landing/referrer on the first page of a visit (fail-open).
import "./utils/growthEvent";
import LoadingScreen from "./components/LoadingScreen";

// ============================================================
// CODE SPLITTING — every route below is lazy-loaded so the main
// bundle only carries the router shell + HomePage (the LCP-critical
// entry page). Before this, App.js eagerly imported EVERY feature UI
// (TechPackUI alone is 7.5k lines) into a 1.87 MB main bundle that
// every marketing-page visitor had to download. Each route now gets
// its own chunk, fetched on first navigation and cached by hash.
// NOTE: scripts/prerender-blogs.js only string-templates
// build/index.html (no SSR), so lazy routes are safe for prerender.
// ============================================================

// HomePage stays EAGER: it's the highest-traffic entry and lazy-loading
// it would add a chunk round-trip before LCP.
import HomePage from "./pages/HomePage";

// Public content pages
const BlogsPage = lazy(() => import("./pages/BlogsPage"));
const InsightsPage = lazy(() => import("./pages/InsightsPage"));
const InsightArticle = lazy(() => import("./pages/InsightArticle"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));

// Auth boundary + dashboard shell (pulls in @clerk/clerk-react — keeping
// these lazy keeps clerk-react out of the public-page bundle entirely)
const ClerkBoundary = lazy(() => import("./components/ClerkBoundary"));
const DashboardLayout = lazy(() => import("./components/layout/DashboardLayout"));
const DashboardHomePage = lazy(() => import("./pages/DashboardHomePage"));
const LibraryPage = lazy(() => import("./pages/LibraryPage"));
const ThreadsPage = lazy(() => import("./pages/ThreadsPage"));
const ThreadStylePage = lazy(() => import("./pages/ThreadStylePage"));
const SharedThreadPage = lazy(() => import("./pages/SharedThreadPage"));
const SketchTeaserPage = lazy(() => import("./pages/SketchTeaserPage"));
// Admin-only guard for unreleased soft-PLM routes (lazy like the pages it
// wraps — it pulls in the Clerk-backed useIsAdmin hook)
const AdminRoute = lazy(() => import("./components/AdminRoute"));
// Growth telemetry (2026-09-02): the tech pack → paid funnel, admin-only.
const GrowthDashboardPage = lazy(() => import("./pages/GrowthDashboardPage"));

// Feature Components
const MoodBoardUI = lazy(() => import("./components/MoodBoardUI"));
const GarmentEditorUI = lazy(() => import("./components/GarmentEditorUI"));
const VariationGeneratorUI = lazy(() => import("./components/VariationGeneratorUI"));
const ProductViewGeneratorUI = lazy(() => import("./components/ProductViewGeneratorUI"));
const PhotoshootUINew = lazy(() => import("./components/PhotoshootUINew"));
const EcommercePhotoshoot = lazy(() => import("./components/Photoshoot/EcommercePhotoshoot"));
const InstagramPhotoshootUI = lazy(() => import("./components/Photoshoot/InstagramPhotoshootUI"));
const MasterCatalogUI = lazy(() => import("./components/MasterCatalogUI"));
const TechPackUI = lazy(() => import("./components/TechPackUI"));
const ColorChangerUI = lazy(() => import("./components/ColorChangerUI"));
const ReFabricUI = lazy(() => import("./components/ReFabricUI"));
const ProfilePage = lazy(() => import("./components/ProfilePage"));
const SignInPage = lazy(() => import("./components/SignInPage"));
const SignUpPage = lazy(() => import("./components/SignUpPage"));
const PlansPage = lazy(() => import("./components/PlansPage"));
const PlanChangeConfirmationPage = lazy(() => import("./components/PlanChangeConfirmationPage"));
const PaymentSuccess = lazy(() => import("./components/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./components/PaymentCancel"));

// Marketing landing pages
const PhotoshootsLandingPage = lazy(() => import("./pages/PhotoshootsLandingPage"));
const TechPackLandingPage = lazy(() => import("./pages/TechPackLandingPage"));
// Sample packs and templates share one document page (2026-09-16): the teaser design, full packs, prerendered.
const TechPackDocumentPage = lazy(() => import("./pages/TechPackDocumentPage"));
const TechPackTemplatesHubPage = lazy(() => import("./pages/TechPackTemplatesHubPage"));
const ColorChangerLandingPage = lazy(() => import("./pages/ColorChangerLandingPage"));
const DesignerLandingPage = lazy(() => import("./pages/DesignerLandingPage"));
const ProductTaggerLandingPage = lazy(() => import("./pages/ProductTaggerLandingPage"));
const VariationsLandingPage = lazy(() => import("./pages/VariationsLandingPage"));
const GarmentEditorLandingPage = lazy(() => import("./pages/GarmentEditorLandingPage"));
const ProductViewLandingPage = lazy(() => import("./pages/ProductViewLandingPage"));
const ReFabricLandingPage = lazy(() => import("./pages/ReFabricLandingPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const TestGarmentCategorization = lazy(() => import("./pages/TestGarmentCategorization"));
const HarmanChawlaAuthorPage = lazy(() => import("./pages/HarmanChawlaAuthorPage"));
const ProgrammaticPage = lazy(() => import("./pages/ProgrammaticPage"));
const ProgrammaticHub = lazy(() => import("./pages/ProgrammaticHub"));

// Embeddable free-tool widgets
const EmbedPricingCalculator = lazy(() => import("./embed/EmbedPricingCalculator"));
const EmbedSizeChartGenerator = lazy(() => import("./embed/EmbedSizeChartGenerator"));
const EmbedDropPlanner = lazy(() => import("./embed/EmbedDropPlanner"));

function App() {
  return (
    <Router>
      <Helmet>
        <link rel="canonical" href={`https://www.adstronaut.net${window.location.pathname}`} />
      </Helmet>
      <Analytics />
      <ScrollToTop />
      <FeatureContextTracker />
      {/* Suspense boundary for all lazy route chunks. Chunks are small,
          hashed, and immutable-cached, so the fallback shows only on the
          FIRST visit to a route in a session — subsequent visits are
          instant from cache. */}
      <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public Homepage */}
        <Route path="/" element={<HomePage />} />
        
        {/* Blog Pages */}
        <Route path="/blogs" element={<BlogsPage />} />
        <Route path="/insights" element={<InsightsPage />} />
        <Route path="/insights/:slug" element={<InsightArticle />} />
        <Route path="/blog/:slug" element={<BlogPost />} />

        {/* Author bio pages */}
        <Route path="/author/harman-chawla" element={<HarmanChawlaAuthorPage />} />
        
        {/* Public Feature Landing Pages - using consistent slugs */}
        <Route path="/ai-photoshoots" element={<PhotoshootsLandingPage />} />
        <Route path="/tech-pack-generator" element={<TechPackLandingPage />} />
        {/* Public sample tech pack view — no auth required, indexable by Google.
            Slug-keyed page rendering a watermarked, simulated-progress reveal
            of a canonical sample tech pack. */}
        <Route path="/tech-pack/sample/:slug" element={<TechPackDocumentPage mode="sample" />} />
        {/* PDF-bundle library — hub + per-template SEO landings. The hub is
            where Reddit "here are N free templates" drops link. Each /:slug
            page has no-spinner iframe + Download PDF + JSON-LD DigitalDocument
            schema so the underlying PDF surfaces in document search results. */}
        <Route path="/tech-pack-templates" element={<TechPackTemplatesHubPage />} />
        <Route path="/tech-pack-templates/:slug" element={<TechPackDocumentPage mode="template" />} />
        <Route path="/garment-recolor" element={<ColorChangerLandingPage />} />
        <Route path="/ai-designer" element={<DesignerLandingPage />} />
        <Route path="/product-tagger" element={<ProductTaggerLandingPage />} />
        <Route path="/design-variations" element={<VariationsLandingPage />} />
        <Route path="/garment-editor" element={<GarmentEditorLandingPage />} />
        <Route path="/product-view-generator" element={<ProductViewLandingPage />} />
        <Route path="/fabric-swapper" element={<ReFabricLandingPage />} />
        <Route path="/products" element={<ProductsPage />} />

        {/* ============================================================
            PROGRAMMATIC GEO/AEO CONTENT PILLARS — public, Clerk-free,
            prerendered (see scripts/prerender-blogs.js). Hub pages list each
            pillar; /:slug renders a single content piece via the shared
            ProgrammaticPageRenderer. /free-tools is scaffolded now; the real
            embedded tools get built per-slug later.
            ============================================================ */}
        <Route path="/compare" element={<ProgrammaticHub pillar="compare" />} />
        <Route path="/compare/:slug" element={<ProgrammaticPage pillar="compare" />} />
        <Route path="/alternatives" element={<ProgrammaticHub pillar="alternatives" />} />
        <Route path="/alternatives/:slug" element={<ProgrammaticPage pillar="alternatives" />} />
        <Route path="/use-cases" element={<ProgrammaticHub pillar="use-cases" />} />
        <Route path="/use-cases/:slug" element={<ProgrammaticPage pillar="use-cases" />} />
        <Route path="/glossary" element={<ProgrammaticHub pillar="glossary" />} />
        <Route path="/glossary/:slug" element={<ProgrammaticPage pillar="glossary" />} />
        <Route path="/fix" element={<ProgrammaticHub pillar="fix" />} />
        <Route path="/fix/:slug" element={<ProgrammaticPage pillar="fix" />} />
        <Route path="/free-tools" element={<ProgrammaticHub pillar="free-tools" />} />
        <Route path="/free-tools/:slug" element={<ProgrammaticPage pillar="free-tools" />} />

        {/* Embeddable free-tool widgets — iframe-safe, no nav/footer/Clerk.
            Carries a do-follow backlink; see vercel.json frame-ancestors header. */}
        <Route path="/embed/clothing-pricing-calculator" element={<EmbedPricingCalculator />} />
        <Route path="/embed/size-chart-generator" element={<EmbedSizeChartGenerator />} />
        <Route path="/embed/fashion-drop-planner" element={<EmbedDropPlanner />} />

        {/* Public read-only shared thread (Soft-PLM §5.7) — token-keyed,
            Clerk-free so a factory on a phone loads no auth bundle. */}
        <Route path="/t/:token" element={<SharedThreadPage />} />

        {/* Outbound teaser page (growth/100-SUBSCRIBERS/75 §3) — token-keyed,
            Clerk-free, unlisted (noindex header in vercel.json). */}
        <Route path="/sketch/:token" element={<SketchTeaserPage />} />

        {/* Legal Pages */}
        <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/legal/terms-of-service" element={<TermsOfService />} />
        
        {/* ============================================================
            AUTH-GATED ROUTES — wrapped in <ClerkBoundary> so clerk-js only
            loads here, NOT on the public marketing/blog pages above. Every
            route that calls a Clerk hook or renders a Clerk component must
            live inside this boundary. Public pages stay Clerk-free.
            ============================================================ */}
        <Route element={<ClerkBoundary />}>
          {/* Sign In/Up Routes */}
          <Route path="/sign-in/*" element={<SignInPage />} />
          <Route path="/sign-up/*" element={<SignUpPage />} />

          {/* Payment Result Pages */}
          <Route path="/paymentsuccess" element={<PaymentSuccess />} />
          <Route path="/paymentcancel" element={<PaymentCancel />} />
          <Route path="/plan-change-confirmation" element={<PlanChangeConfirmationPage />} />

          {/* Plans/Pricing Page (uses Clerk for current-plan + checkout) */}
          <Route path="/plans" element={<PlansPage />} />

          {/* Protected Routes within DashboardLayout */}
          <Route element={<DashboardLayout />}>
            <Route path="/dashboard" element={<DashboardHomePage />} />
            <Route path="/moodboards" element={<MoodBoardUI />} />
            <Route path="/editor" element={<GarmentEditorUI />} />
            <Route path="/variations" element={<VariationGeneratorUI />} />
            <Route path="/product-view" element={<ProductViewGeneratorUI />} />
            <Route path="/photoshoots" element={<PhotoshootUINew />} />
            <Route path="/photoshoots/ecommerce" element={<EcommercePhotoshoot />} />
            <Route path="/photoshoots/instagram" element={<InstagramPhotoshootUI />} />
            <Route path="/catalog" element={<MasterCatalogUI />} />
            {/* Soft-PLM surfaces — admin-only until release (see AdminRoute) */}
            <Route path="/library" element={<AdminRoute><LibraryPage /></AdminRoute>} />
            <Route path="/threads" element={<AdminRoute><ThreadsPage /></AdminRoute>} />
            <Route path="/threads/:styleId" element={<AdminRoute><ThreadStylePage /></AdminRoute>} />
            <Route path="/admin/growth" element={<AdminRoute><GrowthDashboardPage /></AdminRoute>} />
            <Route path="/tech-pack" element={<TechPackUI />} />
            <Route path="/color-changer" element={<ColorChangerUI />} />
            <Route path="/refabric" element={<ReFabricUI />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/test-categorization" element={<TestGarmentCategorization />} />
          </Route>
        </Route>

        {/* Redirect any other unknown routes to home page */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
